import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/router';
import { Scroll, NavigationEnd, UrlTree, RouterModule, Router } from '@angular/router';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, inject, EnvironmentInjector, NgModule } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map, filter, tap } from 'rxjs/operators';
const NavigateTo = destination => {
  return function (target, propertyKey, descriptor) {
    if (typeof target === 'object' && typeof target[propertyKey] === 'function') {
      const original = target[propertyKey];
      descriptor.value = async function (...args) {
        const router = NgDockRouterModule.instance().get(RouterService);
        const location = destination || (await original.call(this, ...args));
        router.navigateTo(location);
      };
    }
  };
};
const NavigateBack = destination => {
  return function (target, propertyKey, descriptor) {
    if (typeof target === 'object' && typeof target[propertyKey] === 'function') {
      const original = target[propertyKey];
      descriptor.value = async function (...args) {
        const router = NgDockRouterModule.instance().get(RouterService);
        const location = destination || (await original.call(this, ...args));
        if (location) {
          return router.back(location.path, location.options);
        }
        return router.back();
      };
    }
  };
};
const NavigationProvider = new InjectionToken('NAVIGATION_PROVIDER');
let RouterService = /*#__PURE__*/(() => {
  class RouterService {
    constructor(navigator) {
      this.navigator = navigator;
      const providerValidation = 'back' in this.navigator && 'navigate' in this.navigator;
      if (!providerValidation) {
        throw new Error(`'NavigationProvider' needs to be an instance of 'NavigationI'`);
      }
    }
    navigate(path, extras) {
      return this.navigator.navigate(path, extras);
    }
    navigateTo(destination) {
      return this.navigate(destination.path, destination.options);
    }
    back(defaultHref, extras) {
      return this.navigator.back(defaultHref, extras);
    }
  }
  RouterService.ɵfac = function RouterService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || RouterService)(i0.ɵɵinject(NavigationProvider));
  };
  RouterService.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: RouterService,
    factory: RouterService.ɵfac
  });
  return RouterService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NavigationBaseService = /*#__PURE__*/(() => {
  class NavigationBaseService {
    constructor(router, location) {
      this.router = router;
      this.location = location;
      this.state$ = new BehaviorSubject({
        direction: undefined,
        replace: false
      });
      this.stack = [];
      this.router.events.pipe(map(event => {
        if (event instanceof Scroll && event.routerEvent instanceof NavigationEnd) {
          return event.routerEvent;
        }
        return event;
      }), filter(event => event instanceof NavigationEnd), tap(event => this.handleStack(event))).subscribe(() => {});
    }
    navigate(url, options) {
      if (options?.direction === 'back') {
        return this.handleNavigation(url, {
          ...options,
          replaceUrl: true,
          direction: 'back'
        });
      }
      return this.handleNavigation(url, options);
    }
    back(defaultHref, extras) {
      const state = {
        ...extras,
        replaceUrl: true,
        direction: 'back'
      };
      this.handleNavigationState(state);
      if (this.stack.length > 1) {
        const target = this.stack[this.stack.length - 2];
        return this.navigate(target.path, {
          ...state,
          queryParams: {
            ...state.queryParams,
            ...target.query
          },
          direction: 'back'
        });
      } else if (defaultHref) {
        return this.navigate(defaultHref, {
          direction: 'back'
        });
      }
      return this.location.back();
    }
    handleNavigationState(extras) {
      const replace = extras?.replaceUrl || false;
      const direction = extras?.direction || 'forward';
      this.state$.next({
        replace,
        direction
      });
    }
    handleNavigation(url, options) {
      this.handleNavigationState(options);
      if (typeof url === 'string' || url instanceof UrlTree) {
        return this.router.navigateByUrl(url, options);
      }
      return this.router.navigate(url, options);
    }
    getUrl(tree) {
      const root = tree.root.children['primary'];
      const path = root?.segments.map(segment => segment.path).join('/') || '';
      return `/${path}`;
    }
    handleStack(e) {
      const {
        direction,
        replace
      } = this.state$.value;
      if (direction === 'back' || replace) {
        this.stack.pop();
      }
      const targetUrl = this.router.parseUrl(e.urlAfterRedirects);
      const path = this.getUrl(targetUrl);
      if (this.stack.length > 0) {
        const previousInstance = this.stack.findIndex(e => e.path === path);
        if (previousInstance > -1) {
          this.stack.splice(previousInstance);
        }
      }
      this.stack.push({
        path: path,
        query: targetUrl.queryParams
      });
    }
  }
  NavigationBaseService.ɵfac = function NavigationBaseService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || NavigationBaseService)(i0.ɵɵinject(i1.Router), i0.ɵɵinject(i2.Location));
  };
  NavigationBaseService.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: NavigationBaseService,
    factory: NavigationBaseService.ɵfac
  });
  return NavigationBaseService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgDockRouterModule = /*#__PURE__*/(() => {
  class NgDockRouterModule {
    static instance() {
      return NgDockRouterModule._instance;
    }
    constructor() {
      this.injector = inject(EnvironmentInjector);
      this.defaultNavigation = inject(NavigationProvider);
      NgDockRouterModule._instance = this;
    }
    get(token) {
      return this.injector.runInContext(() => {
        return inject(token);
      });
    }
  }
  NgDockRouterModule.ɵfac = function NgDockRouterModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || NgDockRouterModule)();
  };
  NgDockRouterModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: NgDockRouterModule
  });
  NgDockRouterModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [RouterService, {
      provide: NavigationProvider,
      useClass: NavigationBaseService
    }],
    imports: [CommonModule, RouterModule]
  });
  return NgDockRouterModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const getRouteStack = router => {
  let routes = [];
  let stack = [router.routerState.snapshot.root];
  while (stack.length > 0) {
    const route = stack.pop();
    routes.push(route);
    stack.push(...route.children);
  }
  return routes;
};
const getTopActivatedRoute = route => {
  if (route.children?.length > 0) {
    return getTopActivatedRoute(route.children[0]);
  }
  return route;
};
const getActivatedRoute = router => getTopActivatedRoute(router.routerState.root);
const getRouteProperties = (router, key) => getRouteStack(router).reduce((query, route) => {
  return {
    ...query,
    ...route[key]
  };
}, {});
const createDecoratorFn = type => paramName => {
  return function (target, propertyKey, some) {
    if (typeof target === 'object') {
      let originalValue = target[propertyKey];
      Reflect.defineProperty(target, propertyKey, {
        get: () => {
          const router = NgDockRouterModule.instance().get(Router);
          const activeRoute = getActivatedRoute(router);
          if (activeRoute?.component?.constructor instanceof target.constructor) {
            throw Error(`${target.constructor} is not activated`);
          }
          const params = activeRoute.snapshot[type];
          if (paramName) {
            return typeof params[paramName] === 'undefined' ? originalValue : params[paramName];
          }
          return params || originalValue;
        },
        set: val => {
          originalValue = val;
        },
        enumerable: true,
        configurable: true
      });
    }
  };
};
const Param = createDecoratorFn('params');
const Query = createDecoratorFn('queryParams');
const Data = createDecoratorFn('data');

/*
 * Public API Surface of router
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Data, NavigateBack, NavigateTo, NavigationProvider, NgDockRouterModule, Param, Query, RouterService };
